import { useParams, Link } from 'react-router-dom';
import { allContent, categories } from '@/mocks/siteData';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = allContent.find((item) => item.id === Number(id));

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <h2 className="font-heading text-xl font-bold text-foreground-900 mb-2">内容不存在</h2>
          <Link to="/" className="text-primary-600 text-sm hover:underline">返回首页</Link>
        </div>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedItems = allContent
    .filter((item) => item.category === article.category && item.id !== article.id)
    .slice(0, 4);

  const fullDescription = `${article.summary}

《${article.title}》（${article.titleEn}）是一部${article.year}年出品的精彩${article.categoryName}作品，为广大日韩影视爱好者带来了极致的观影体验。本作品配有精校中文字幕，确保观众能够准确理解剧情发展和人物对白。

作为中文字幕日韩影视大全平台重点推荐的${article.categoryName}内容，《${article.title}》自上线以来获得了观众的高度评价，评分高达${article.rating}分。如果您喜欢${article.tags?.join('、')}类型的作品，那么这部作品绝对不容错过。

平台提供高清中文字幕日韩资源在线观看，支持多种设备流畅播放。无论您是在家中使用电脑，还是在外出途中使用手机，都可以随时随地畅享原汁原味的中文字幕日韩影视体验。我们承诺持续更新优质日韩中字内容，让您的追剧之旅永不断档。`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${article.title} - ${article.titleEn} 中文字幕在线观看`,
    description: article.summary,
    datePublished: `${article.year}-01-01`,
    author: {
      '@type': 'Organization',
      name: '中文字幕日韩影视大全',
    },
    publisher: {
      '@type': 'Organization',
      name: '中文字幕日韩影视大全',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://grcsaas.cn/article/${article.id}`,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <main className="pt-16 min-h-screen bg-background-50">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: category?.name || article.categoryName, to: category ? `/category/${category.slug}` : undefined },
            { label: article.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-shrink-0">
                <div className="relative w-48 md:w-56 aspect-[2/3] rounded-xl overflow-hidden border border-background-200/70">
                  <img
                    src={article.imageUrl}
                    alt={`${article.title} - ${article.titleEn} 中文字幕版`}
                    title={`${article.title} ${article.categoryName}中文字幕`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <span className="text-xs bg-primary-100 text-primary-700 px-2.5 py-1 rounded-full font-medium whitespace-nowrap">
                    {article.categoryName}
                  </span>
                  <span className="text-xs bg-background-100 text-foreground-600 px-2.5 py-1 rounded-full whitespace-nowrap">
                    {article.year}
                  </span>
                  <span className="text-xs bg-accent-100 text-accent-700 px-2.5 py-1 rounded-full font-semibold whitespace-nowrap">
                    {article.rating} 分
                  </span>
                </div>

                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                  {article.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-6">{article.titleEn}</p>

                {'updateInfo' in article && (
                  <div className="inline-flex items-center gap-1.5 bg-accent-50 text-accent-700 px-3 py-1.5 rounded-lg text-sm font-medium mb-6 whitespace-nowrap">
                    <i className="ri-time-line"></i>
                    {article.updateInfo}
                  </div>
                )}

                {article.tags && (
                  <div className="flex items-center gap-2 mb-6 flex-wrap">
                    {article.tags.map((tag: string) => (
                      <span
                        key={tag}
                        className="text-xs bg-secondary-100 text-secondary-700 px-2.5 py-1 rounded-full whitespace-nowrap"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                <div className="bg-background-100 rounded-xl p-5 border border-background-200/70">
                  <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
                    {fullDescription}
                  </p>
                </div>
              </div>
            </div>

            {relatedItems.length > 0 && (
              <div className="mt-14">
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-5">
                  相关推荐
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {relatedItems.map((item) => (
                    <Link
                      key={item.id}
                      to={`/article/${item.id}`}
                      className="group flex flex-col bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                    >
                      <div className="relative w-full aspect-[2/3] overflow-hidden">
                        <img
                          src={item.imageUrl}
                          alt={`${item.title} - ${item.titleEn}`}
                          title={`${item.title} 中文字幕`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                        />
                        <div className="absolute top-2 left-2 bg-primary-500 text-white text-xs font-medium px-2 py-0.5 rounded-md whitespace-nowrap">
                          {item.rating}
                        </div>
                      </div>
                      <div className="p-3">
                        <h4 className="font-heading font-semibold text-sm text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors">
                          {item.title}
                        </h4>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
    </>
  );
}